import { Box, Button, Card, CardMedia, CircularProgress, Container, Dialog, FormControl, Grid, IconButton, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { validate } from '../../api/services/auth';
import { useNavigate } from 'react-router-dom';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../utils/errorHandler';
import { ADMIN_CURRENT_NAME, ADMIN_STORAGE_KEY } from '../../utils/constants';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import Header from '../../layouts/Header/Header';
import { getPrograms } from '../../api/services/program';
import { Program } from '../../api/interfaces/Program';
import PinnacleDrawer from '../../components/PinnacleDrawer/PinnacleDrawer';
import { getModifiedFieldsOnly } from '../../utils/getModifiedFieldsOnly';
import { FormData, useReferenceForm } from './hooks/useReferenceForm';
import { uploadFile } from '../../api/services/file';
import PinnacleConfirmDeleteItem from '../../components/PinnacleConfirmDeleteItem/PinnacleConfirmDeleteItem';
import { createReference, deleteReference, getReferenceById, getReferences, updateReference } from '../../api/services/reference';
import { Reference } from '../../api/interfaces/Reference';
import SkeletonReference from '../../components/skeleton/SkeletonReference/SkeletonReference';
import { PinnacleSearchField } from '../../components/PinnacleSearchField/PinnacleSearchField';
import { useDebounce } from 'use-debounce';
import RearrangeReferencesDrawerContent from './partials/RearrangeReferencesDrawerContent';
import CreateReferenceDrawerContent from './partials/CreateReferenceDrawerContent/CreateReferenceDrawerContent';
import EditReferenceDrawerContent from './partials/EditReferenceDrawerContent/EditReferenceDrawerContent';

const s3BucketName = 'references';

const ReferencesPage: React.FC = () => {
    const navigate = useNavigate();

    // Initial form data
    const initialFormData = {
        programId: '',
        title: '',
        description: '',
        thumbnail: '',
        content: '',
        isTrial: false,
        isVisible: true
    };

    // Initial touch fields for error validation
    const initialTouchFields = {
        programId: false,
        title: false,
        description: false,
        thumbnail: false,
        content: false,
        isTrial: false,
        isVisible: false
    };

    // Hooks for form and validation states
    const {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid
    } = useReferenceForm({
        initialFormData,
    });

    const [currentAdminName, setCurrentAdminName] = useState<string>('');
    const [programs, setPrograms] = useState<Program[]>([]);
    const [references, setReferences] = useState<Reference[]>([]);

    // Search references with debounce
    const [searchReference, setSearchReference] = useState<string>('');
    const [debouncedSearchReference] = useDebounce(searchReference, 300);

    const [isReferencesLoading, setIsReferencesLoading] = useState<boolean>(false);
    const [isReferenceDeleting, setIsReferenceDeleting] = useState<boolean>(false);
    const [imageErrorMap, setImageErrorMap] = useState<Record<string, boolean>>({});

    // Drawable states for create and edit
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [currentDrawableState, setCurrentDrawableState] = useState<string>('');
    const [currentDrawableTitle, setCurrentDrawableTitle] = useState<string>('');

    // Set the selected program id for initial and filter state
    const [selectedProgramId, setSelectedProgramId] = useState<string>('');
    const [selectedProgramTitle, setSelectedProgramTitle] = useState<string>('');

    // Set the selected reference id for edit state
    const [selectedReferenceId, setSelectedReferenceId] = useState<string>('');
    const [selectedReferenceTitle, setSelectedReferenceTitle] = useState<string>('');

    // This will be used in the edit wherein we will identify the textfields
    // changes compare by comparing to this original form data
    const [originalFormData, setOriginalFormData] = useState(initialFormData);

    // Confirm delete dialog state
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);

    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Use the correct type for the ref to access handleSubmit
    const createEditReferenceRef = useRef<{ handleSubmit: () => void } | null>(null);

    // Submit status state
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Loading state for edit button
    const [isEditClick, setIsEditClick] = useState<boolean>(false);

    const handleImageError = (url: string) => {
        setImageErrorMap((prev) => ({ ...prev, [url]: true }));
    };

    const fetchReferences = useCallback(async (programId: string, keyword?: string) => {
        try {
            const referencesResponse = await getReferences({ programId, keyword });
            setReferences(referencesResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    // Initial fetch
    const fetchProgramReferences = useCallback(async () => {
        try {
            const programResponse = await getPrograms();
            if (programResponse.data.length > 0) {
                const firstProgram = programResponse.data[0];
                setSelectedProgramId(firstProgram._id);
                setSelectedProgramTitle(firstProgram.name);

                // Retreive the first record right ahead
                await fetchReferences(firstProgram._id);
            }
            setPrograms(programResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, [fetchReferences]);

    const fetchSearchResults = useCallback(async (programId: string, searchTerm: string) => {
        try {
            setIsReferencesLoading(true);
            await fetchReferences(programId, searchTerm);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsReferencesLoading(false);
        } finally {
            setIsReferencesLoading(false);
        }
    }, [fetchReferences]);

    const handleFilterReferenceByProgramChange = async (event: SelectChangeEvent<string>) => {
        try {
            setSearchReference('');
            setSelectedProgramId(event.target.value);
            setSelectedProgramTitle(programs.find(program => program._id === event.target.value)?.name || '');
            setIsReferencesLoading(true);
            await fetchReferences(event.target.value);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsReferencesLoading(false);
        } finally {
            setIsReferencesLoading(false);
        }
    };

    const handleReferenceCallback = async (programId: string) => {
        try {
            setSearchReference('');
            setIsReferencesLoading(true);
            await fetchReferences(programId);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsReferencesLoading(false);
        } finally {
            setIsReferencesLoading(false);
        }
    }

    // Handle the opening and closing of toggle
    const toggleDrawer = (open: boolean, state: 'rearrange' | 'create' | 'edit' | string) => () => {
        if (open) {
            setCurrentDrawableState(state);
            setCurrentDrawableTitle(state === 'create' ? 'Add New Reference' : state === 'edit' ? 'Update References' : state === 'rearrange' ? 'Rearrange References' : '');
        } else {
            if (state === 'edit') {
                resetForm();
            }
            setCurrentDrawableState('');
        }

        setIsDrawerOpen(open);
    };

    const modifiedFields = useMemo(
        () => getModifiedFieldsOnly(formData, originalFormData),
        [formData, originalFormData]
    );

    const isFormModified = Object.keys(modifiedFields).length > 0;

    const handleEdit = async (selectedReference: Reference) => {
        try {
            setSelectedReferenceId(selectedReference._id);
            setSelectedReferenceTitle(selectedReference.title);
            setIsEditClick(true);
            const referenceInfo = await getReferenceById(selectedReference._id);
            const { programId, title, description, thumbnail, content, isTrial, isVisible } = referenceInfo.data;
            const referenceData: FormData = {
                programId,
                title,
                description,
                thumbnail,
                content,
                isTrial,
                isVisible
            };
            setOriginalFormData(referenceData);
            resetForm(referenceData);
            toggleDrawer(true, 'edit')();
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsEditClick(false);
        } finally {
            setIsEditClick(false);
        }
    };

    const handleCreateSubmit = async () => {
        setIsSubmitting(true);
        try {
            let thumbnail = formData.thumbnail;

            if (selectedImage) {
                const file = selectedImage;
                const uploadResponse = await uploadFile(s3BucketName, file);
                thumbnail = uploadResponse.data.location;

                setRawFormData((prev) => ({ ...prev, thumbnail }));
                setFormData((prev) => ({ ...prev, thumbnail }));
            }

            const payload = {
                ...formData,
                thumbnail: thumbnail,
            };
            const updateResponse = await createReference(payload);

            // Only apply if it's currently selected in the list view
            if (updateResponse.data.programId === selectedProgramId) {
                setReferences((prev) => [updateResponse.data, ...prev]);
            }
            pushNotification({
                details: updateResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
            setSelectedImage(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleUpdateSubmit = async () => {
        setIsSubmitting(true);
        try {
            let thumbnail = formData.thumbnail;

            if (selectedImage) {
                const file = selectedImage;
                const uploadResponse = await uploadFile(s3BucketName, file);
                thumbnail = uploadResponse.data.location;

                setRawFormData((prev) => ({ ...prev, thumbnail }));
                setFormData((prev) => ({ ...prev, thumbnail }));
            }

            const payload = {
                ...formData,
                thumbnail: thumbnail,
            };
            const updateResponse = await updateReference(selectedReferenceId, payload);

            setReferences((prev) =>
                prev.map((ref) =>
                    ref._id === selectedReferenceId ? updateResponse.data : ref
                )
            );
            pushNotification({
                details: updateResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
            setSelectedImage(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleDelete = (reference: Reference) => {
        setSelectedReferenceId(reference._id);
        setSelectedReferenceTitle(reference.title);
        setIsConfirmDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsReferenceDeleting(true);
        try {
            const deleteResponse = await deleteReference(selectedReferenceId);
            setReferences(prevReferences => prevReferences.filter(ref => ref._id !== selectedReferenceId));
            pushNotification({
                details: deleteResponse.message,
                type: NotificationTypes.Success,
            });
            setIsConfirmDeleteDialogOpen(false);
            setSelectedReferenceTitle('');
            setSelectedReferenceId('');
            setIsReferenceDeleting(false);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsReferenceDeleting(false);
        } finally {
            setIsReferenceDeleting(false);
        }
    }

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedImage(file);
            setRawFormData((prev) => ({
                ...prev,
                thumbnail: 'selected',
            }));
        }
    };

    const handleDrawerClose = () => {
        toggleDrawer(false, currentDrawableState)()
        setRawFormData(initialFormData);
        setFormData(initialFormData);
        setTouchedFields(initialTouchFields);
        setCurrentDrawableState('');
        setIsSubmitting(false);

        // Clear file input and image
        setSelectedImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            setIsReferencesLoading(true);
            const token =
                localStorage.getItem(ADMIN_STORAGE_KEY) ||
                sessionStorage.getItem(ADMIN_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            const cachedName = localStorage.getItem(ADMIN_CURRENT_NAME);
            if (cachedName) {
                setCurrentAdminName(cachedName);
            }
            try {
                const validateResponse = await validate();
                const firstName = validateResponse.data.firstName;
                if (!cachedName) {
                    localStorage.setItem(ADMIN_CURRENT_NAME, firstName);
                }
                setCurrentAdminName(firstName);
                await fetchProgramReferences();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setIsReferencesLoading(false);
            }
        };
        run();
    }, [fetchProgramReferences, navigate]);

    // Debouncing mechanism for search reference
    useEffect(() => {
        const search = async () => {
            if (!selectedProgramId) return;

            setIsReferencesLoading(true);
            try {
                const trimmed = debouncedSearchReference.trim();
                if (trimmed !== '') {
                    await fetchSearchResults(selectedProgramId, debouncedSearchReference);
                } else {
                    await fetchReferences(selectedProgramId);
                }
            } finally {
                setIsReferencesLoading(false);
            }
        };

        search();
    }, [debouncedSearchReference, fetchSearchResults, fetchReferences, selectedProgramId]);

    return (
        <main className="main-content">
            <Sidebar />

            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Header title='References' currentAdminName={currentAdminName} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 4,
                            flexWrap: 'wrap',
                            gap: '2'
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ mr: 2 }}
                            onClick={toggleDrawer(true, 'create')}
                            disabled={isReferencesLoading}
                        >
                            New Reference
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<DragIndicatorIcon />}
                            sx={{ mr: 2 }}
                            onClick={toggleDrawer(true, 'rearrange')}
                            disabled={isReferencesLoading}
                        >
                            Rearrange Reference
                        </Button>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <PinnacleSearchField
                                id="searchReferences"
                                label="Search reference here..."
                                value={searchReference}
                                onChange={setSearchReference}
                                isLoading={isReferencesLoading}
                            // disabled={isReordering}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
                            <Typography variant="body1" fontWeight={600}>
                                List of Reference {selectedProgramTitle && `under ${selectedProgramTitle}`}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ color: '#818181' }}>
                                    view by
                                </Typography>
                                <FormControl>
                                    <Select
                                        value={selectedProgramId}
                                        onChange={handleFilterReferenceByProgramChange}
                                        defaultValue={selectedProgramId}
                                        // disabled={(loadingMode === 'searching' || loadingMode === 'filtering' || isReordering)}
                                        displayEmpty
                                        inputProps={{ 'aria-label': 'Select Program' }}
                                        sx={{
                                            minWidth: 250,
                                            maxWidth: 250,
                                            color: '#818181',
                                            '.MuiSelect-icon': {
                                                color: '#818181',
                                            },
                                        }}
                                        MenuProps={{
                                            disableScrollLock: true,
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                        }}
                                    >
                                        {programs.map((program) => (
                                            <MenuItem key={program._id} value={program._id}>
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: 'block',
                                                    }}
                                                >
                                                    {program.name}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        <Grid container rowSpacing={1} columnSpacing={1}>
                            {isReferencesLoading ? (
                                Array.from({ length: 10 }).map((_, idx) => (
                                    <Grid size={{ xs: 4, md: 2.4, lg: 2.4 }} key={idx}>
                                        <SkeletonReference />
                                    </Grid>
                                ))
                            ) : references.length > 0 ?
                                (
                                    references.map((reference: Reference) => (
                                        <Grid size={{ xs: 4, md: 2.4, lg: 2.4 }} key={reference._id}>
                                            <Card
                                                sx={{
                                                    borderRadius: 3,
                                                    boxShadow: 3,
                                                    bgcolor: "background.paper",
                                                    p: 0.75,
                                                    overflow: "visible",
                                                    position: "relative",
                                                    "&:hover .hover-actions": { opacity: 1, transform: "translateY(0)" },
                                                }}
                                            >

                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        borderRadius: 2,
                                                        overflow: "hidden",
                                                        height: {
                                                            xs: 160,
                                                            sm: 200,
                                                            md: 240,
                                                            lg: 300
                                                        },
                                                        bgcolor: "grey.100",
                                                    }}
                                                >
                                                    {/* Thumbnail / Fallback */}
                                                    {imageErrorMap[reference.thumbnail] ? (
                                                        <Box
                                                            sx={{
                                                                height: "100%",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                typography: "caption",
                                                                color: "text.secondary",
                                                                px: 2,
                                                                textAlign: "center",
                                                            }}
                                                        >
                                                            {reference.title}
                                                        </Box>
                                                    ) : (
                                                        <CardMedia
                                                            component="img"
                                                            alt={reference.title}
                                                            image={reference.thumbnail}
                                                            sx={{ height: "100%", width: "100%", objectFit: "cover" }}
                                                            onError={() => handleImageError(reference.thumbnail)}
                                                        />
                                                    )}

                                                    {/* Hover actions (edit/delete) */}
                                                    <Box
                                                        className="hover-actions"
                                                        sx={{
                                                            position: "absolute",
                                                            top: 8,
                                                            right: 8,
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            gap: 0.5,
                                                            opacity: 0,
                                                            transform: "translateY(-4px)",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEdit(reference)}
                                                            sx={{
                                                                bgcolor: "rgba(255,255,255,0.95)",
                                                                borderRadius: 1.5,
                                                                boxShadow: 2,
                                                                p: 0.5,
                                                                "&:hover": {
                                                                    bgcolor: "rgba(255,255,255,1)",
                                                                },
                                                            }}
                                                        >
                                                            {/* Only show the loader for the clicked item */}
                                                            {isEditClick && reference._id === selectedReferenceId ? <CircularProgress size={17} /> : <ModeEditIcon fontSize="small" />}
                                                        </IconButton>

                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete(reference)}
                                                            sx={{
                                                                bgcolor: "rgba(255,255,255,0.95)",
                                                                borderRadius: 1.5,
                                                                boxShadow: 2,
                                                                p: 0.5,
                                                                "&:hover": {
                                                                    bgcolor: "rgba(255,255,255,1)",
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            bgcolor: "#000000CC",
                                                            color: "common.white",
                                                            px: 1.25,
                                                            py: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                lineHeight: 1.15,
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 1,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {reference.title}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                opacity: 0.9,
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 1,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {reference.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    ))
                                )
                                : (
                                    <Grid size={12}>
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            height="200px"
                                            width="100%"
                                        >
                                            <Typography variant="body1" color="text.secondary">
                                                No references available.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                        </Grid>
                    </Box>
                </Container>
            </Box>

            <PinnacleDrawer
                title={currentDrawableTitle}
                isDrawerOpen={isDrawerOpen}
                onClose={() => handleDrawerClose()}
                customWidthSize={(currentDrawableState === 'create' || currentDrawableState === 'edit') ? 700 : undefined}
                onSubmit={async () => {
                    if (createEditReferenceRef.current?.handleSubmit) {
                        await createEditReferenceRef.current.handleSubmit();
                    }
                }}
                onSubmitName={currentDrawableState === 'edit' ? 'UPDATE INFORMATION' : currentDrawableState === 'create' ? 'CREATE' : ''}
                isSubmitting={isSubmitting}
            >
                {currentDrawableState === 'rearrange' && (
                    <RearrangeReferencesDrawerContent
                        programs={programs}
                        firstProgramId={selectedProgramId}
                        firstProgramTitle={selectedProgramTitle}
                        closeToggle={toggleDrawer(false, '')}
                        onReferencesCallback={handleReferenceCallback}
                    />
                )}

                {currentDrawableState === 'create' && (
                    <CreateReferenceDrawerContent
                        ref={createEditReferenceRef}
                        programs={programs}
                        formData={rawFormData}
                        rawFormData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleCreateSubmit}
                        isSubmitting={isSubmitting}
                        isFormValid={isFormValid}
                        fileInputRef={fileInputRef}
                        handleUploadClick={handleUploadClick}
                        handleFileChange={handleFileChange}
                        selectedImage={selectedImage}
                        closeToggle={toggleDrawer(false, '')}
                        onReferencesCallback={handleReferenceCallback}
                        setIsSubmitting={setIsSubmitting}
                    />
                )}

                {currentDrawableState === 'edit' && (
                    <EditReferenceDrawerContent
                        ref={createEditReferenceRef}
                        programs={programs}
                        formData={rawFormData}
                        rawFormData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleUpdateSubmit}
                        isSubmitting={isSubmitting}
                        isFormValid={isFormValid}
                        fileInputRef={fileInputRef}
                        handleUploadClick={handleUploadClick}
                        handleFileChange={handleFileChange}
                        selectedImage={selectedImage}
                        closeToggle={toggleDrawer(false, '')}
                        onReferencesCallback={handleReferenceCallback}
                        setIsSubmitting={setIsSubmitting}
                        isFormModified={isFormModified}
                    />
                )}
            </PinnacleDrawer>

            <Dialog
                open={isConfirmDeleteDialogOpen}
                onClose={() => setIsConfirmDeleteDialogOpen(!isConfirmDeleteDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete Program"
                    itemToDelete={selectedReferenceTitle}
                    onClose={() => setIsConfirmDeleteDialogOpen(false)}
                    onDelete={handleConfirmDelete}
                    onCancel={() => setIsConfirmDeleteDialogOpen(false)}
                    isDeleting={isReferenceDeleting}
                />
            </Dialog>
        </main>
    );
};

export default ReferencesPage;
